import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DateTime } from 'luxon';
import {
  AllFieldsDTO,
  CreateFieldTemplate,
  DeleteProcessStageData,
  DeleteSectionsData,
  EFieldType,
  FieldSectionDTO,
  ProcessStageDTO,
} from '../../../../shared/Field/Field';
import { APIDocEvents } from '../../../../shared/events/APIDocumentEvents';
import { EIdTagsForAPIs } from '../../../../shared/ProcessIdsTags';
import { config } from '../../../core';
import { ECustomEventsNames } from '../../../../shared/events/CustomEvents';

const dateTransform = (date: string) => DateTime.fromISO(date).toMillis();

const baseQuery = fetchBaseQuery({
  baseUrl: new URL(`${config.api.prefix}process-fields`, config.api.origin).href,
  prepareHeaders: (headers) => {
    headers.set('Authorization', `Bearer ${localStorage.getItem('token') ?? sessionStorage.getItem('token')}`);
    return headers;
  },
});

// Define a service using a base URL and expected endpoints
export const processFieldsApi = createApi({
  reducerPath: 'PROCESS_FIELDS_API',
  baseQuery,
  tagTypes: [...Object.values(EFieldType), 'ALL'],
  endpoints: (builder) => {
    const fieldDataUpdated = new Event(APIDocEvents.PROCESS_FIELDS_DATA_UPDATE, { bubbles: true });
    const dispatchUpdateEvent = () => {
      document.dispatchEvent(fieldDataUpdated);
    };
    const dispatchChangeSourceEvent = (id?: string) => {
      if (id) {
        document.dispatchEvent(new CustomEvent(`${ECustomEventsNames.CHANGE_FIELD_SOURCE}-${id}`));
      }
    };

    return {
      getProcessFields: builder.query < { [key:string]: AllFieldsDTO }, { pointerId: string, pointerType: 'process' | 'template' }>({
        query: ({ pointerId, pointerType }) => `${pointerId}/get-fields/${pointerType}`,
        providesTags: (result, error, { pointerId }) => [{ type: 'ALL', id: pointerId }],
      }),

      getStagesTemplatesByOrgId: builder.query<ProcessStageDTO[], { organizationId: string, filter?: string }>({
        query: (data: { organizationId: string, filter?: string }) => ({
          method: 'POST',
          url: 'stages/get-templates',
          body: data,
        }),
        transformResponse: (res: ProcessStageDTO[]): ProcessStageDTO[] => res.sort(
          ({ updatedAt: a }, { updatedAt: b }) => dateTransform(b) - dateTransform(a),
        ),
        providesTags: () => [{ type: EFieldType.STAGE, id: EIdTagsForAPIs.TEMPLATE }],
      }),

      getSectionTemplatesByOrgId: builder.query<FieldSectionDTO[], { organizationId?: string, filter?: string }>({
        query: (data: { organizationId?: string, filter?: string }) => ({
          method: 'POST',
          url: 'sections/get-templates',
          body: data,
        }),
        transformResponse: (res: FieldSectionDTO[]): FieldSectionDTO[] => res.sort(
          ({ updatedAt: a }, { updatedAt: b }) => dateTransform(b) - dateTransform(a),
        ),
        providesTags: () => [{ type: EFieldType.SECTION, id: EIdTagsForAPIs.TEMPLATE }],
      }),
      deleteSection: builder.mutation<void, DeleteSectionsData>({
        query: (data: DeleteSectionsData) => ({
          method: 'POST',
          url: `${data.processId}/fields/delete-sections`,
          body: data,
        }),
        invalidatesTags: (result, error, { parentId }) => {
          dispatchChangeSourceEvent(parentId);
          return [
            { type: EFieldType.SECTION, id: parentId },
            { type: EFieldType.SECTION, id: EIdTagsForAPIs.TEMPLATE },
          ];
        },
        onCacheEntryAdded: dispatchUpdateEvent,
      }),
      createFieldTemplate: builder.mutation<ProcessStageDTO | FieldSectionDTO, CreateFieldTemplate>({
        query: (data: CreateFieldTemplate) => ({
          method: 'POST',
          url: 'fields/create-template',
          body: data,
        }),
        invalidatesTags: (result, error, { type }) => [{ type, id: EIdTagsForAPIs.TEMPLATE }],
      }),
      deleteStage: builder.mutation<void, DeleteProcessStageData>({
        query: (data: DeleteProcessStageData) => ({
          method: 'POST',
          url: `${data.processId}/fields/delete-stage`,
          body: data,
        }),
        invalidatesTags: (result, error, { stageId }) => [
          { type: EFieldType.STAGE, id: stageId },
          { type: EFieldType.STAGE, id: EIdTagsForAPIs.TEMPLATE },
        ],
        onCacheEntryAdded: dispatchUpdateEvent,
      }),
      createTemplateFromStage: builder.mutation<ProcessStageDTO, { id: string, processId: string, title?: string }>({
        query: (data: { id: string, processId: string, title: string }) => ({
          method: 'POST',
          url: `${data.processId}/fields/create-template-from-stage`,
          body: data,
        }),
        invalidatesTags: () => [{ type: EFieldType.STAGE, id: EIdTagsForAPIs.TEMPLATE }],
      }),
      createTemplateFromSection: builder.mutation<ProcessStageDTO, { id: string, processId: string, title?: string }>({
        query: (data: { id: string, processId: string, title: string }) => ({
          method: 'POST',
          url: `${data.processId}/fields/create-template-from-section`,
          body: data,
        }),
        invalidatesTags: () => [{ type: EFieldType.SECTION, id: EIdTagsForAPIs.TEMPLATE }],
      }),
      changeTemplateTimeStamp: builder.mutation<void, { id: string, type: EFieldType }>({
        query: (data: { id: string, type: EFieldType }) => ({
          method: 'PATCH',
          url: 'fields/update-timestamp',
          body: data,
        }),
        invalidatesTags: (res, err, { type }) => [{ type, id: EIdTagsForAPIs.TEMPLATE }],
      }),
    };
  },
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useGetProcessFieldsQuery,
  useDeleteSectionMutation,
  useDeleteStageMutation,
  useCreateTemplateFromStageMutation,
  useCreateTemplateFromSectionMutation,
  useGetStagesTemplatesByOrgIdQuery,
  useGetSectionTemplatesByOrgIdQuery,
  useCreateFieldTemplateMutation,
  useChangeTemplateTimeStampMutation,
} = processFieldsApi;
